import { loadPendingImages } from './pendingImageStorage';

const MONGO_ID_RE = /^[a-f\d]{24}$/i;

export const isMongoMediaId = (id) =>
  typeof id === 'string' && MONGO_ID_RE.test(id);

/** Stable client/server id for an image row in the grid. */
export const getImageKey = (img) => {
  if (!img) return null;
  return img.id || img._id || null;
};

/** Mongo media id when the row exists in the database. */
export const getMediaMongoId = (img) => {
  if (!img) return null;
  if (img._id && isMongoMediaId(String(img._id))) return String(img._id);
  if (img.id && isMongoMediaId(String(img.id))) return String(img.id);
  return null;
};

export const isImageGenerated = (img) =>
  !!(img?.meta?.aws_upload?.s3_url || img?.AIUrl);

/** Interior pages that still need line art output. */
export const getImagesMissingLineart = (images) =>
  (images || []).filter((img) => {
    if (!isImageGenerated(img)) return true;

    const inputKey = img.croppedStagingKey || img.stagingKey || img.sourceStagingKey;
    const storedKey = img.lineartInputStagingKey || null;
    if (inputKey && storedKey && inputKey !== storedKey) {
      return true;
    }

    return false;
  });

export const findPendingEntry = (img, pendingImages) => {
  if (!img || !pendingImages?.length) return null;
  const key = getImageKey(img);
  return (
    pendingImages.find(
      (p) =>
        p.id === key ||
        p.id === img.id ||
        p.id === img._id ||
        (img._id && p.id === String(img._id)),
    ) || null
  );
};

export const isImageSentToRun = (img, pendingImages) => {
  const pending = findPendingEntry(img, pendingImages);
  return !!pending?.sentToRun;
};

export const isImageUploading = (img, uploadingImages) => {
  const key = getImageKey(img);
  if (!key || !uploadingImages?.length) return false;
  return (
    uploadingImages.includes(key) ||
    (img._id && uploadingImages.includes(img._id)) ||
    (img.id && uploadingImages.includes(img.id))
  );
};

/**
 * True when this image still needs to be sent to RunPod line art.
 * Skips finished AI output, in-flight uploads, and rows already queued.
 */
export const needsLineartGeneration = (img, { pendingImages, uploadingImages }) => {
  if (!img || isImageUploading(img, uploadingImages)) return false;
  if (isImageGenerated(img)) return false;
  // Re-crop after generation clears AIUrl — always queue again even if sentToRun is stale.
  if (img.isCropped) return true;
  if (isImageSentToRun(img, pendingImages)) return false;
  return true;
};

/** Images that should be included in the next /run payload. */
export const getUploadableImages = (images, bookId, uploadingImages) => {
  if (!images?.length) return [];
  const pendingImages = bookId ? loadPendingImages(bookId) : [];
  return images.filter((img) =>
    needsLineartGeneration(img, { pendingImages, uploadingImages }),
  );
};

export const allImagesGenerated = (images) =>
  images.length > 0 && images.every(isImageGenerated);

export const hasCroppedCloudinaryUrl = (img) => {
  if (img?.croppedStagingKey) return true;
  if (!img?.croppedCloudinaryUrl) return false;
  if (typeof img.croppedCloudinaryUrl === 'object') {
    return !!(img.croppedCloudinaryUrl.url || img.croppedCloudinaryUrl.publicId);
  }
  return typeof img.croppedCloudinaryUrl === 'string' && img.croppedCloudinaryUrl.length > 0;
};

/** True while a cropped Cloudinary URL is being uploaded after auto/manual crop. */
export const isPhotoCropPrepInProgress = (img) =>
  !!img?.isLoadingCover &&
  !isImageGenerated(img) &&
  !hasCroppedCloudinaryUrl(img);

/** The crop was cut here but saving it failed, even after the quiet retries. Next tries again. */
export const isPhotoCropPrepFailed = (img) => !!img?.cropUploadFailed;

/**
 * A photo cropped on this device whose crop never reached the server: its save
 * failed, or the page was reloaded before it finished (the server row has no
 * cropped copy; the crop box came across from this device's saved list). Next
 * saves these before the drawing starts. Left alone, the server would draw a
 * centre crop instead of the one the customer saw and checked.
 */
export const needsCropSave = (img) => {
  if (!img || isImageGenerated(img) || hasCroppedCloudinaryUrl(img)) return false;
  if (isPhotoCropPrepInProgress(img)) return false;
  if (img.cropUploadFailed) return true;
  if (img.isCropped && img.file instanceof Blob) return true;
  return !!(img.cropData && img.sourceStagingKey);
};

/**
 * Block Choose Photos Next while a page that needs line art is still having its
 * crop saved. A failed save doesn't block (decided 2026-11-02): the customer
 * could never get past it. Next saves it again instead (needsCropSave).
 */
export const isChoosePhotosNextBlockedByCropPrep = (
  images,
  bookId,
  uploadingImages,
) => {
  if (!images?.length) return false;

  const pendingImages = bookId ? loadPendingImages(bookId) : [];

  return images.some((img) => {
    if (!needsLineartGeneration(img, { pendingImages, uploadingImages })) {
      return false;
    }
    if (isPhotoCropPrepInProgress(img)) return true;
    if (isPhotoCropPrepFailed(img)) return false;
    if (hasCroppedCloudinaryUrl(img)) return false;
    // Auto-cropped in memory — wait for Cloudinary prep (same-session blob fallback exists on Next if prep skipped).
    if (
      img.isCropped &&
      (img.file instanceof Blob || img.url?.startsWith('blob:'))
    ) {
      return true;
    }
    return false;
  });
};

/**
 * True while line art is being generated for this book (RunPod in flight).
 * Used to lock Choose Photos against edits that would duplicate pages.
 */
export const isLineartGenerationActive = ({
  bookId,
  lineartProgress,
  activeBook,
  pendingImages: pendingImagesParam,
  uploadingImages,
  images,
  serverRows,
}) => {
  if (!bookId) return false;

  const pendingImages =
    pendingImagesParam ??
    (bookId ? loadPendingImages(bookId) : []);

  const hasInFlightPending = pendingImages.some((p) => {
    if (!p.sentToRun) return false;

    if (images?.length) {
      const gridImg = images.find(
        (img) =>
          String(getImageKey(img)) === String(p.id) ||
          (img._id && String(img._id) === String(p.id)),
      );
      if (gridImg && isImageGenerated(gridImg) && !gridImg.isGenerating) {
        return false;
      }
    }

    return true;
  });
  const hasUploadingQueue =
    Array.isArray(uploadingImages) && uploadingImages.length > 0;

  // The server's own word: a page it is drawing has isGenerating and no art
  // yet. `serverRows` are its fresh pages (useBookPagesFromServer); the grid
  // (loaded from the server on entry) stands in until they arrive. Holds when
  // every local flag above was lost (a reload, a remount that pruned pending
  // storage, a missed event).
  const serverDrawing = (serverRows || images || []).some(
    (img) => img?.isGenerating === true && !isImageGenerated(img),
  );

  const reduxProcessing = lineartProgress?.isProcessing === true;

  // Only lock for *line art* generation. We require positive proof the active
  // job is line art (processingWorkflowType === 'lineart'); a missing/unknown
  // type or a 'cover' job must never lock the photo step. (Cover generation
  // shares the same isProcessing flag, and the field can be absent on older
  // API responses or legacy books.)
  const dbProcessing =
    activeBook?.isProcessing === true &&
    activeBook?.processingWorkflowType === 'lineart' &&
    (activeBook?.processingPercentage ?? 0) < 100;

  return (
    reduxProcessing ||
    dbProcessing ||
    hasInFlightPending ||
    hasUploadingQueue ||
    serverDrawing
  );
};

/** Index of an image in the Choose Photos grid (0-based). */
export const getGridIndex = (img, orderedImages) => {
  if (!img || !orderedImages?.length) return -1;
  const key = getImageKey(img);
  if (!key) return -1;
  return orderedImages.findIndex((row) => {
    const rowKey = getImageKey(row);
    return (
      rowKey === key ||
      (img._id && row._id && String(row._id) === String(img._id))
    );
  });
};

/** Attach grid position before sending to RunPod. */
export const tagUploadableWithGridOrder = (uploadableImages, orderedImages) =>
  uploadableImages.map((img) => {
    const gridOrdering = getGridIndex(img, orderedImages);
    return gridOrdering >= 0 ? { ...img, gridOrdering } : img;
  });
